/**
 * Deliberately duplicated, UX-only validator — a hand-written mirror of
 * backend/utils/landingPageSchema.js's validateNodeTree(), used ONLY for
 * instant feedback in the builder (disabling Publish, flagging a node
 * red). It is NOT the security boundary: every write still goes through
 * the backend's zod schema + validateNodeTree() before persisting, and
 * this copy can lag or be subtly wrong without that being a security bug
 * (see plan Decision #6). If you change the node-type allowlist, depth/
 * count limits, or authRef rules here, make the same change in
 * backend/utils/landingPageSchema.js — they intentionally do not share
 * an import (CommonJS backend vs. CRA frontend, no shared-package wiring).
 */
import { COMPONENT_REGISTRY, AUTH_SMART_TYPES } from './componentRegistry';

export const MAX_NODES = 300;
export const MAX_DEPTH = 8;

/**
 * @param {object[]} nodes - root-level node array
 * @returns {{valid: true, nodeCount: number} | {valid: false, errors: string[]}}
 */
export function validateNodeTree(nodes) {
    const errors = [];
    const seenIds = new Set();
    let count = 0;

    function walk(node, depth) {
        count++;
        if (count > MAX_NODES) {
            errors.push(`Page exceeds the maximum of ${MAX_NODES} components`);
            return;
        }
        if (depth > MAX_DEPTH) {
            errors.push(`Component "${node.id}" exceeds the maximum nesting depth of ${MAX_DEPTH}`);
            return;
        }
        if (!COMPONENT_REGISTRY[node.type]) {
            errors.push(`Unsupported component type "${node.type}"`);
        }
        if (seenIds.has(node.id)) {
            errors.push(`Duplicate component id "${node.id}"`);
        }
        seenIds.add(node.id);

        const def = COMPONENT_REGISTRY[node.type];
        const isContainer = !!def?.isContainer;
        const isAuthSmart = AUTH_SMART_TYPES.includes(node.type);

        if (!isContainer && node.children && node.children.length > 0) {
            errors.push(`Component type "${node.type}" (id "${node.id}") cannot contain children`);
        }
        if (isAuthSmart && !node.authRef) {
            errors.push(`Component "${node.id}" of type "${node.type}" requires an authRef`);
        }
        if (!isAuthSmart && node.authRef) {
            errors.push(`Component "${node.id}" of type "${node.type}" must not have an authRef`);
        }
        if (node.layers && node.type !== 'background') {
            errors.push(`Component type "${node.type}" (id "${node.id}") cannot have background layers`);
        }

        if (isContainer && node.children) {
            for (const child of node.children) walk(child, depth + 1);
        }
    }

    for (const root of nodes) walk(root, 1);

    if (errors.length > 0) return { valid: false, errors };
    return { valid: true, nodeCount: count };
}
